import { unitNoun } from "../Negocio/catalog";
export const sellsToFinal = (product) => product?.sellToFinal !== false && product?.sellToFinal !== 0;
export const sellsToNegocio = (product) => product?.sellToReseller === true || product?.sellToReseller === 1 || product?.sellToReseller === "1";

/**
 * El servidor arma el catálogo según audiencia:
 * open = cliente nuevo (todos), final / reseller = clasificado.
 * Aquí solo se refuerza si el perfil ya es revendedor y el catálogo aún no.
 */
export function storeCatalogProducts(products = [], { audience, customerType } = {}) {
  if (customerType === "reseller" && audience !== "reseller") return [];
  if (audience === "reseller") return products.filter(sellsToNegocio);
  if (audience === "open") return products;
  return products.filter(sellsToFinal);
}
export function categoryOf(product) {
  if (product.fulfillmentType === "giftcard") return "Gift cards";
  const name = `${product.name || ""} ${product.planLabel || ""}`;
  const brand = name.match(/netflix|disney\+?|prime\s*video|amazon|hbo|max\b|spotify|youtube|crunchyroll|paramount\+?|apple\s*tv|iptv|vix|canva|office|microsoft/i);
  if (brand) return brand[0].replace(/^amazon$/i, "Prime Video").replace(/^hbo$/i, "Max");
  return ({ recharge: "Recargas", mailbox: "Correos", license: "Licencias", device: "Televisión" })[product.fulfillmentType] || "Otros servicios";
}
export function formatMoney(value, currency = "COP", symbol) {
  if (symbol) return `${symbol}${new Intl.NumberFormat("es-CO", { maximumFractionDigits: ["COP", "CLP", "PYG"].includes(currency) ? 0 : 2 }).format(Number(value || 0))}`;
  return new Intl.NumberFormat("es-CO", { style: "currency", currency, maximumFractionDigits: ["COP", "CLP", "PYG"].includes(currency) ? 0 : 2 }).format(Number(value || 0));
}
export function assetUrl(value) {
  if (!value) return "";
  const raw = String(value);
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^(data:|javascript:|vbscript:|\/\/)/i.test(raw)) return "";
  const base = String(process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
  return `${base}/public/${raw.replace(/^\/?public\//, "").replace(/^\//, "")}`;
}
export const safeExternalUrl = value => /^https?:\/\//i.test(String(value || "")) ? value : null;
export const statusLabel = status => ({ awaiting_payment: "Pendiente de pago", payment_review: "Pago en revisión", special_request: "Pedido especial", fulfilled: "Entregado", manual_activation: "Activación en proceso", paid: "Pago confirmado", cancelled: "Cancelado", active: "Activo", expired: "Vencido", pending: "Pendiente", draft: "En preparación" })[status] || status;
export function requestKey() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes).map(b => b.toString(16).padStart(2, "0")).join("");
}

/** Group actual plans for presentation; the cart always receives the original product. */
export function groupStoreProducts(products = []) {
  const groups = new Map();
  products.forEach(product => {
    const name = String(product.fulfillmentType === "giftcard" ? `${product.giftCardBrand || product.name} · Gift cards` : product.name || "Producto").trim().replace(/\s+/g, " ");
    const key = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    if (!groups.has(key)) groups.set(key, { name, variants: [] });
    groups.get(key).variants.push(product);
  });
  const priority = [/netflix/i, /disney/i, /prime|amazon/i, /youtube/i, /paramount/i];
  const rank = name => { const index = priority.findIndex(pattern => pattern.test(name)); return index < 0 ? priority.length : index; };
  return Array.from(groups.values()).map(group => {
    const variants = [...group.variants].sort((a, b) => {
      const availableA = a.stock !== 0 && a.accountPricing?.local > 0;
      const availableB = b.stock !== 0 && b.accountPricing?.local > 0;
      return Number(availableB) - Number(availableA) || (Number(a.accountPricing?.local || Infinity) - Number(b.accountPricing?.local || Infinity)) || a.id - b.id;
    });
    const available = variants.filter(product => product.stock !== 0 && product.accountPricing?.local > 0);
    const first = available[0] || variants[0];
    return { ...first, featured: variants.some(product => !!product.featured), name: group.name, imageUrl: first.imageUrl || variants.find(product => product.imageUrl)?.imageUrl || "", planLabel: `${variants.length} ${first.fulfillmentType === "giftcard" ? "variantes por región y valor" : variants.length === 1 ? "plan disponible" : "planes para elegir"}`, accountPricing: available.length ? { ...first.accountPricing, varies: true } : null, stock: available.some(product => product.stock == null) ? null : available.reduce((sum, product) => sum + Number(product.stock || 0), 0), variants };
  }).sort((a, b) => Number(!!b.featured) - Number(!!a.featured) || rank(a.name) - rank(b.name) || a.name.localeCompare(b.name));
}

export function currencyName(code) {
  try { return `${new Intl.DisplayNames(["es"], { type: "currency" }).of(code)} (${code})`; }
  catch { return code; }
}
export function giftCardRegion(product) {
  const code = product.originCountry;
  if (code === "GLOBAL") return "Global";
  if (code === "LATAM") return "Latinoamérica";
  if (code === "EU") return "Europa";
  try { return new Intl.DisplayNames(["es"], { type: "region" }).of(code) || code; } catch { return code || "Región por confirmar"; }
}
const UNIT_WORDS = { profile: "perfil", member: "miembro", device: "dispositivo", user: "usuario", panel: "panel" };
export function variantLabel(product) {
  if (product.fulfillmentType === "giftcard") return `${product.giftCardValue} ${product.giftCardCurrency} · ${giftCardRegion(product)} · Código de un solo uso`;
  const unit = UNIT_WORDS[unitNoun(product)];
  const kind = product.sellFullAccount && product.sellByProfile ? `Cuenta completa / ${unit}`
    : product.sellFullAccount ? "Cuenta completa" : product.sellByProfile ? `Por ${unit}`
    : product.slotKind === "none" ? "Cuenta completa / servicio" : "Acceso individual";
  return [kind, product.planLabel, product.durationDays > 0 ? `${product.durationDays} días` : ""].filter(Boolean).join(" · ");
}
export function productPrice(product, store, mode = "local") {
  const pricing = product.accountPricing || {};
  const local = Number(pricing.local || 0);
  const fx = Number(pricing.fx || 0);
  const listLocal = Number(pricing.listLocal || 0);
  const listFx = Number(pricing.listFx || 0);
  const rate = Number(store.exchangeRate);
  const promoPercent = Number(pricing.promoPercent || 0);
  if (mode === "fx") {
    const amount = fx > 0 ? fx : rate > 0 ? Math.round(local / rate * 100) / 100 : null;
    const list = listFx > 0 ? listFx : listLocal > 0 && rate > 0 ? Math.round(listLocal / rate * 100) / 100 : null;
    return { amount, list: list > 0 && amount != null && list > amount ? list : null, promoPercent: promoPercent > 0 ? promoPercent : null, currency: store.currencyFx };
  }
  return {
    amount: local,
    list: listLocal > local ? listLocal : null,
    promoPercent: promoPercent > 0 ? promoPercent : null,
    currency: store.currency
  };
}

/** Pedido mínimo (unidades). 1 = sin mínimo; el aviso se muestra desde 2. */
export const minOrderOf = product => Math.max(1, Math.floor(Number(product?.minOrderQty) || 1));
/** Tope por producto: 5, o el mínimo si es mayor. */
export const maxOrderOf = product => Math.max(5, minOrderOf(product));
export const minOrderNotice = product => (minOrderOf(product) >= 2 ? `Pedido mínimo: ${minOrderOf(product)} unidades` : "");
/** Cantidades que se pueden elegir en el carrito (desde el mínimo hasta el tope o el stock). */
export function quantityOptions(product) {
  const min = minOrderOf(product);
  const cap = Math.min(maxOrderOf(product), product?.stock == null ? maxOrderOf(product) : Number(product.stock));
  return cap < min ? [] : Array.from({ length: cap - min + 1 }, (_, i) => min + i);
}
